import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, FileText, CheckCircle2, Clock, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

export default function VerifyProfessional() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(undefined); // undefined=loading, null=none, object=exists
  const [fullName, setFullName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [regNo, setRegNo] = useState("");
  const [hospital, setHospital] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      setFullName(user.name || "");
      api.get("/verification-requests/me").then(({ data }) => setReq(data)).catch(() => setReq(null));
    }
  }, [user]);

  if (user === null) return <div className="p-4"><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  if (user === false) {
    return (
      <div className="p-8 text-center">
        <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-slate-300" />
        <p className="text-sm text-slate-500">Sign in to request professional verification.</p>
        <Button onClick={() => navigate("/login")} className="mt-3 bg-medorange hover:bg-medorange-hover">Sign in</Button>
      </div>
    );
  }

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setDocUrl(data.url);
      toast.success("Document attached");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/verification-requests", {
        full_name: fullName, specialty, registration_no: regNo, hospital, document_url: docUrl || null,
      });
      toast.success("Request submitted for admin review");
      setReq({ ...data, full_name: fullName, specialty, registration_no: regNo, hospital, document_url: docUrl });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  const Status = ({ status }) => {
    const map = {
      pending: { icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200", label: "Pending review" },
      approved: { icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Approved" },
      rejected: { icon: XCircle, color: "text-red-600 bg-red-50 border-red-200", label: "Not approved" },
    }[status] || {};
    const Icon = map.icon || Clock;
    return <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${map.color}`}><Icon className="h-4 w-4" /> {map.label}</span>;
  };

  return (
    <div className="px-4 py-4 pb-8" data-testid="verify-professional-page">
      <button onClick={() => navigate("/more")} className="mb-3 flex items-center gap-1 text-xs text-slate-400 hover:text-navy"><ArrowLeft className="h-3 w-3" /> Back</button>
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <h1 className="font-heading text-xl font-extrabold text-slate-900">Get Verified</h1>
          <p className="text-xs text-slate-500">Submit your credentials for the Verified Professional badge</p>
        </div>
      </div>

      {user.professional_verified ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-emerald-500" />
          <p className="font-heading text-sm font-bold text-emerald-800">You're a Verified Professional</p>
          <p className="mt-1 text-xs text-emerald-700">Your identity has been confirmed by MedLync.</p>
        </div>
      ) : req === undefined ? (
        <Skeleton className="h-64 w-full rounded-2xl" />
      ) : req && req.status === "pending" ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <Status status="pending" />
          <p className="mt-3 text-sm text-slate-600">Thanks, {req.full_name}. Your verification request is being reviewed by the MedLync team. You'll get the Verified Pro badge once approved.</p>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            <p><strong>Specialty:</strong> {req.specialty}</p>
            <p><strong>Registration No:</strong> {req.registration_no}</p>
            {req.hospital && <p><strong>Hospital:</strong> {req.hospital}</p>}
          </div>
        </div>
      ) : (
        <>
          {req && req.status === "rejected" && (
            <div className="mb-3"><Status status="rejected" /><p className="mt-2 text-xs text-slate-500">Your previous request wasn't approved. You can submit again with updated details.</p></div>
          )}
          <form onSubmit={submit} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
            <div><Label className="text-xs font-semibold text-slate-600">Full name (as on license)</Label>
              <Input data-testid="verify-fullname-input" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1" required /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Specialty / Role</Label>
              <Input data-testid="verify-specialty-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="e.g. Cardiology, Apollo Ahmedabad" className="mt-1" required /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Medical registration number</Label>
              <Input data-testid="verify-regno-input" value={regNo} onChange={(e) => setRegNo(e.target.value)} placeholder="e.g. GMC/12345" className="mt-1" required /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Hospital / Institution (optional)</Label>
              <Input value={hospital} onChange={(e) => setHospital(e.target.value)} className="mt-1" /></div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Supporting document (optional)</Label>
              <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-slate-300 p-3 text-sm text-slate-500 hover:border-medorange">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {docUrl ? "Document attached ✓" : "Upload license / ID (image or PDF)"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadDoc} />
              </label>
              {docUrl && <a href={fileUrl(docUrl)} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-medorange">View attached document</a>}
            </div>
            <Button data-testid="verify-submit-button" type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for verification"}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
